import { ExclamationCircleFilled } from '@ant-design/icons';
import DriveIcon from '@assets/icons/Icon-awesome-google-drive.png';
import HomeworkIcon from '@assets/icons/Icon-awesome-home.png';
import { useModal } from '@hooks';

import { CreateHomeworkModal } from '@modules/Mentor/reusables';
import { MeetingWithHomework } from '@shared/common';
import { TECH_UPGRADE_EDUCATION_EMAIL } from '@shared/constants';
import { ENVIRONMENT } from '@shared/frontendEnv';
import { useUserDetails } from '@actions';
import { documentUrl } from '@shared/utils/string';
import btn from '@styles/components/reusables/buttons.module.scss';
import { Tooltip } from 'antd';
import { useAnalytics } from 'apps/agora/src/contexts/AnalyticsContext';
import { ANALYTICS_EVENT_NAMES } from 'apps/agora/src/utils/constants';
import React from 'react';
import { Link } from 'react-router-dom';
import { COLORS } from '../../../../../v3/global';
import MeetingListItemButton from './MeetingListItemButton';

interface HomeworkButtonProps {
  meeting: MeetingWithHomework;
  refetch?: () => void;
}

const HomeworkButton: React.FC<HomeworkButtonProps> = (props: HomeworkButtonProps) => {
  const { meeting, refetch } = props;
  const [HomeworkModal, showHomeworkModal] = useModal(CreateHomeworkModal);
  const homework = meeting.homework;

  const { trackEvent } = useAnalytics();

  const { data: user } = useUserDetails(meeting?.students[0]?._id);

  const googleDrive = user?.googleDrive?.[`${ENVIRONMENT}`]?.student?.id;
  const isHomeworkUnavailable = !googleDrive;
  const homeworkLabel = homework?.fileId ? 'View Homework' : 'Add Homework';

  const homeworkUnavailableTooltip = (
    <div className="feedback-button-tooltip flex-col items-start text-left">
      <span>Homework is unavailable because the student Google Drive folder is missing.</span>
      Contact an administrator at:
      <a
        href={`mailto:${TECH_UPGRADE_EDUCATION_EMAIL}`}
        className="mt-2 underline"
        style={{ color: COLORS.PRIMARY }}
      >
        {TECH_UPGRADE_EDUCATION_EMAIL}
      </a>
    </div>
  );

  const handleClick = () => {
    if (isHomeworkUnavailable) {
      return;
    }

    trackEvent(ANALYTICS_EVENT_NAMES.MEETING_ADD_HOMEWORK_BUTTON_CLICK);

    if (!homework) {
      showHomeworkModal({
        enrollmentId: meeting?.enrollment?._id,
        studentId: meeting.students[0]._id,
        meetingId: meeting._id,
        refetch,
      });
    }

    if (homework?.fileId) {
      window.open(documentUrl(homework.fileId), '_blank');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {!isHomeworkUnavailable && (
        <MeetingListItemButton>
          <Link
            className="flex items-center w-full h-full text-white py-2 px-4"
            to={{
              pathname: `https://drive.google.com/drive/folders/${googleDrive}`,
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={DriveIcon} alt="drive-icon" style={{ marginRight: '12px', width: '14px' }} />
            <span className="text-white">Open Drive</span>
          </Link>
        </MeetingListItemButton>
      )}

      <Tooltip title={isHomeworkUnavailable ? homeworkUnavailableTooltip : null} placement="left">
        <div>
          <MeetingListItemButton disabled={isHomeworkUnavailable}>
            <div
              className="flex items-center justify-between w-full h-full text-inherit py-2 px-4"
              onClick={isHomeworkUnavailable ? undefined : handleClick}
            >
              <div className="flex items-center min-w-0">
                <img src={HomeworkIcon} alt="homework-icon" className="mr-3 w-3.5 h-3" />
                <span
                  style={{
                    color: isHomeworkUnavailable ? COLORS.TEXT_SECONDARY : COLORS.TEXT_PRIMARY,
                    whiteSpace: 'nowrap',
                  }}
                  className={`${btn.actionButton} gradient`}
                >
                  {homeworkLabel}
                </span>
              </div>

              {isHomeworkUnavailable && (
                <ExclamationCircleFilled style={{ color: COLORS.ERROR, fontSize: '14px' }} />
              )}
            </div>
          </MeetingListItemButton>
        </div>
      </Tooltip>

      {HomeworkModal}
    </div>
  );
};

export default HomeworkButton;
